'use client'

import { useEffect, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { Skeleton } from '@/components/ui/skeleton'
import { getUpcomingLaunches } from '@/lib/services/productService'

interface UpcomingProduct {
  id: string
  slug: string
  name: string
  tagline: string
  logoUrl: string
  launchDate?: Date
}

export function WeekProductsWidget() {
  const [products, setProducts] = useState<UpcomingProduct[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    getUpcomingLaunches()
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setIsLoading(false))
  }, [])

  if (isLoading) {
    return (
      <div className="bg-card border border-border rounded-2xl p-5">
        <Skeleton className="h-4 w-36 mb-4" />
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-start gap-3">
              <Skeleton className="w-12 h-12 rounded-xl shrink-0" />
              <div className="flex-1">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-3 w-full mt-1.5" />
                <Skeleton className="h-3 w-16 mt-1.5" />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (products.length === 0) return null

  return (
    <div className="bg-card border border-border rounded-2xl p-5">
      <h3 className="text-sm font-semibold text-foreground mb-4">
        Ближайшие запуски
      </h3>
      <div className="space-y-4">
        {products.map((product) => (
          <div key={product.id} className="flex items-start gap-3">
            <Link
              href={`/products/${product.slug}`}
              className="shrink-0"
            >
              <Image
                src={product.logoUrl}
                alt={product.name}
                width={48}
                height={48}
                className="w-12 h-12 rounded-xl object-cover"
              />
            </Link>
            <div className="flex-1 min-w-0">
              <Link href={`/products/${product.slug}`}>
                <h4 className="text-sm font-medium text-foreground hover:text-primary transition-colors line-clamp-1">
                  {product.name}
                </h4>
              </Link>
              <p className="text-xs text-foreground-secondary mt-0.5 line-clamp-2">
                {product.tagline}
              </p>
              {product.launchDate && (
                <p className="text-xs text-foreground-tertiary mt-1">
                  {product.launchDate.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
